import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bem-vindo de volta!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no login");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: "url(/brand/owner.png)",
          backgroundColor: "#000",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <img src="/brand/logo.png" alt="Gregory João" className="w-64 mb-6 drop-shadow-2xl" />
          <h1 className="text-3xl font-light tracking-tight mb-3 font-heading text-amber-100/90">
            Gestão de salão <br />
            sob medida
          </h1>
          <p className="text-sm opacity-80 max-w-md leading-relaxed font-body">
            Agenda, financeiro, lembretes e clientes — tudo em um só lugar.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-2">
            <img src="/brand/logo.png" alt="Gregory João" className="h-12 mb-4 lg:hidden" />
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-heading">
              Gregory João
            </div>
            <h2 className="text-3xl font-light tracking-tight font-heading">Entre na sua conta</h2>
            <p className="text-sm text-muted-foreground font-body">
              Painel de gestão · Confia, eu faço!
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                data-testid="login-email-input"
                className="rounded-md"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest">
                Senha
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
                className="rounded-md"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 rounded-md"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase tracking-widest">
              <span className="bg-background px-2 text-muted-foreground">Ou</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-11 rounded-md"
            onClick={handleGoogleLogin}
            data-testid="login-google-button"
          >
            Continuar com Google
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            Ainda não tem conta?{" "}
            <Link to="/register" className="text-foreground font-medium hover:underline" data-testid="login-register-link">
              Criar conta
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
